import { NetworkIntegrityAggregate, NetworkIntegrityAggregateTableRow } from "@app/projects/network-assessment/network-integrity/models";
import { JSX, useContext, useEffect, useMemo, useState } from "react";
import { CriticalAlertsDescription, NetworkIntegrityAggregateTable, NetworkIntegrityWidgetInfoFlyout, WidgetIcon, WidgetMenu, WidgetTitleBackgroundColor } from "@app/projects/network-assessment/network-integrity/widgets";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityActionStatus } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { NetworkIntegrityAggregateGroup, NetworkIntegrityAggregateKey, NetworkIntegrityJobStatus } from "@app/projects/enumerators";
import { Link } from "react-router-dom";
import { getPendoClassNameForAggregateGroup, getPendoClassNameForDynamicAggregateKey } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityPendoHelper";
import { WidgetLoading, WidgetHeader, Widget, WidgetBody, WidgetFooter } from "./Widget";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { getNetworkIntegrityGhostProviderLobLabel } from "@app/projects/network-assessment/provider-hub/utility";

const CriticalAlertsWidget = (): JSX.Element => {
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const project = networkIntegrityContext.project!;
  const ffContext = useContext(FeatureFlagContext);
  const veryLowOnly = ffContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_VERY_LOW_LOCATION_CONFIDENCE_ONLY);
  const specialtyEvidence = ffContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_SPECIALTY_EVIDENCE);
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);
  const allowProviderDetailView = project.showNetworkIntegrityCriticalAlerts ?? false;

  const [criticalAlertResults, setCriticalAlertResults] = useState<NetworkIntegrityAggregate[]>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const title = 'Critical Alerts';
  const titleIcon = WidgetIcon.Critical;
  const titleBackgroundColor = WidgetTitleBackgroundColor.Red;

  const ghostProviderLobLabel = getNetworkIntegrityGhostProviderLobLabel(project.networkTaxonomy?.lineOfBusiness, project.networkTaxonomy?.lineOfBusinessType);

  const rowOrder: string[] = [
    "Expired License Practitioners",
    ...(ghostProviderLobLabel ? [ghostProviderLobLabel] : []),
    "Ghost Providers (Total Claims)",
    veryLowOnly ? "Very Low Location Confidence" : "Low Location Confidence",
    ...(specialtyEvidence ? [NetworkIntegrityAggregateKey.LowSpecialtyEvidence] : [])
  ];

  const getRowPosition = (aggregateKey: string): number => {
    const position = rowOrder.indexOf(aggregateKey);
    return position === -1 ? rowOrder.length : position;
  };

  useEffect(() => {
      let cancelled = false;
      setCriticalAlertResults(null);
      async function fetchCriticalAlertResults() {
          try {
            if (networkIntegrityContext.projectRunConfigurationId) {
              const result = await networkIntegrityService.getCriticalAlertResults(project.projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes);
              if (!cancelled) setCriticalAlertResults(result);
            }
          } finally {
              if (!cancelled) setLoading(false);
          };
      };

      if (networkIntegrityContext.isJobStatusLoading === false && networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Finished) {
        setLoading(true);
        fetchCriticalAlertResults();
      };

      return () => { cancelled = true; };
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.jobStatus, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes]);

  if (networkIntegrityContext.isLoading || loading || !networkIntegrityContext.jobStatus)
    return (<WidgetLoading><WidgetHeader title={title} titleBackgroundColor={titleBackgroundColor} titleIcon={titleIcon} /></WidgetLoading>);

  const missingRowResults: NetworkIntegrityAggregate[] = rowOrder
    .filter((aggregateKey) => !criticalAlertResults?.some((result) => result.aggregateKey === aggregateKey))
    .map((aggregateKey) => ({ afterTermedCount: 0, aggregateGroup: NetworkIntegrityAggregateGroup.CriticalAlerts, aggregateKey, count: 0 }));

  const rows : NetworkIntegrityAggregateTableRow[] = criticalAlertResults?.length > 0 ? [...criticalAlertResults, ...missingRowResults].map((result) => {
        const getProviderDetailsLink = (count: number, actionStatus?: NetworkIntegrityActionStatus) => {
          return (count === 0)
                              ? <span>0</span>
                              : (allowProviderDetailView ? <Link className={getPendoClassNameForDynamicAggregateKey(result.aggregateKey)}
                                  to={{
                                    pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/providers/${networkIntegrityContext.providerType}/${NetworkIntegrityAggregateGroup.CriticalAlerts}/${encodeURIComponent(result.aggregateKey)}`,
                                    state: {
                                      initialActionStatus: actionStatus,
                                      initialRegionSetIds: networkIntegrityContext.regionSetIds,
                                      initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
                                      runConfiguration: networkIntegrityContext.projectRunConfiguration
                                    }
                                  }}>
                                    {count?.toLocaleString()}
                                </Link>
                                : <span>{count?.toLocaleString()}</span>
                                )
        }
      const countCell = getProviderDetailsLink(result.count);
      const afterTermedCount = getProviderDetailsLink(result.afterTermedCount, NetworkIntegrityActionStatus.NoActionsApplied);
      return {
        afterTermedCount,
        aggregateKey: result.aggregateKey,
        count: countCell,
        rawAfterTermedCount: result.afterTermedCount ?? undefined,
        rawCount: result.count,
      }
  }).sort((a, b) => getRowPosition(a.aggregateKey) - getRowPosition(b.aggregateKey) || a.aggregateKey.localeCompare(b.aggregateKey))
  : rowOrder.map((aggregateKey) => ({aggregateKey}));

  const footer =
    ffContext.enabledFeatureFlags.has(Features.DISPLAY_NETWORK_INTEGRITY_TOTAL_AFTER_TERMED) && 
    allowProviderDetailView &&
    <WidgetFooter>
      <Link className={getPendoClassNameForAggregateGroup(NetworkIntegrityAggregateGroup.CriticalAlerts, "view-all")}
        to={{
          pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/all/${NetworkIntegrityAggregateGroup.CriticalAlerts}`,
          state: {
            initialProviderType: networkIntegrityContext.providerType,
            initialRegionSetIds: networkIntegrityContext.regionSetIds,
            initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
            runConfiguration: networkIntegrityContext.projectRunConfiguration
          }
        }}>
          View All
      </Link>
    </WidgetFooter>;

  return (
    <Widget>
      <WidgetHeader title={title} titleBackgroundColor={titleBackgroundColor} titleIcon={titleIcon}>
        <WidgetMenu>
          <NetworkIntegrityWidgetInfoFlyout title="Critical Alerts" description={<CriticalAlertsDescription/>} showLicenseInfo={!allowProviderDetailView} />
          {criticalAlertResults && allowProviderDetailView && <Link
            className="qes-button qes-button-secondary"
            id="network-integrity-providers__download-report-link"
            to={{
              pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/reports/${networkIntegrityContext.providerType}/${NetworkIntegrityAggregateGroup.CriticalAlerts}`,
              state: {
                reportRowCount: criticalAlertResults.reduce((sum, current) => sum + current.count, 0),
                runConfiguration: networkIntegrityContext.projectRunConfiguration,
                showConfidenceLevel: true,
                showCriticalAlerts: allowProviderDetailView
              }
            }}
            type="button" >
            Create All Report
          </Link>}
        </WidgetMenu>
      </WidgetHeader>
      <WidgetBody>
        <NetworkIntegrityAggregateTable aggregateHeader="Alert" aggregateCountHeader="Unique Access Points" rows={rows} />
      </WidgetBody>
      {footer}
    </Widget>);
}

export { CriticalAlertsWidget };